import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { ArchiveIcon } from '@trello/nachos/icons/archive';
import { CopyIcon } from '@trello/nachos/icons/copy';
import { MoveIcon } from '@trello/nachos/icons/move';
import type { PopoverProps } from '@trello/nachos/popover';
import { Popover, usePopover } from '@trello/nachos/popover';
import { PopoverMenu, PopoverMenuButton } from '@trello/nachos/popover-menu';

import { CopyCardPopover } from 'app/src/components/MoveCardPopover/CopyCardPopover';
import { MoveCardPopover } from 'app/src/components/MoveCardPopover/MoveCardPopover';

export interface InboxBulkActionsContextMenuProps {
  popoverProps: PopoverProps;
}
export const InboxBulkActionsContextMenu: FunctionComponent<
  InboxBulkActionsContextMenuProps
> = ({ popoverProps }) => {
  const {
    popoverProps: bulkMovePopoverProps,
    triggerRef: bulkMoveTriggerRef,
    toggle: toggleBulkMove,
  } = usePopover({
    placement: 'right-start',
  });

  const {
    popoverProps: bulkCopyPopoverProps,
    triggerRef: bulkCopyTriggerRef,
    toggle: toggleBulkCopy,
  } = usePopover({
    placement: 'right-start',
  });

  return (
    <Popover {...popoverProps}>
      <PopoverMenu>
        <PopoverMenuButton
          iconBefore={<MoveIcon />}
          onClick={(e) => {
            e.stopPropagation();
            toggleBulkMove();
          }}
          data-testid="inbox-bulk-actions-move"
        >
          <div ref={bulkMoveTriggerRef}>
            <FormattedMessage
              id="templates.quick_card_editor.move"
              defaultMessage="Move"
              description="Menu option to move selected cards in the inbox bulk actions context menu"
            />
          </div>
        </PopoverMenuButton>

        <PopoverMenuButton
          iconBefore={<CopyIcon />}
          onClick={(e) => {
            e.stopPropagation();
            toggleBulkCopy();
          }}
          data-testid={'inbox-bulk-actions-copy'}
        >
          <div ref={bulkCopyTriggerRef}>
            <FormattedMessage
              id="templates.quick_card_editor.copy"
              defaultMessage="Copy"
              description="Bulk inbox copy card"
            />
          </div>
        </PopoverMenuButton>

        <PopoverMenuButton
          iconBefore={<ArchiveIcon />}
          onClick={(e) => {
            e.stopPropagation();
            // Archive functionality to be implemented
          }}
          data-testid={'inbox-bulk-actions-archive'}
        >
          <FormattedMessage
            id="templates.quick_card_editor.archive"
            defaultMessage="Archive"
            description="Bulk inbox archive card"
          />
        </PopoverMenuButton>
      </PopoverMenu>
      <Popover {...bulkMovePopoverProps}>
        <MoveCardPopover onClose={toggleBulkMove} />
      </Popover>
      <Popover {...bulkCopyPopoverProps}>
        <CopyCardPopover onClose={toggleBulkCopy} />
      </Popover>
    </Popover>
  );
};
